import { SECOND_MS } from '@calm/web-common/dist/time';
import useAppConfig from '@rhg/hooks/use-app-config';
import { authRedirectQueryString } from '@rhg/hooks/use-auth-redirect';
import { isLogoutRedirectSuppressed } from '@rhg/libs/api/client/unauthorized';
import { logger } from '@rhg/libs/logger';
import { routes } from '@rhg/utils/routes';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useE2eAuth } from '../e2e-auth-provider';
import {
    Auth0ContextInterface,
    GetTokenSilentlyOptions,
    useAuth0,
    User,
    GenericError,
    RedirectLoginOptions,
} from '../index';

type UseAuthReturnType = Auth0ContextInterface<User>;

type GetAccessTokenSilently = UseAuthReturnType['getAccessTokenSilently'];
type GetTokenSilentlyVerboseResponse = Exclude<
    Awaited<ReturnType<GetAccessTokenSilently>>,
    string
>;

type Push = ReturnType<typeof useRouter>['push'];

const buildLoginUrl = (returnTo: string): string => {
    if (!returnTo || returnTo === '/') {
        return `${routes.login}`;
    }
    return `${routes.login}?${authRedirectQueryString}=${encodeURIComponent(
        returnTo
    )}`;
};

const ONEID_KEY = 'https://oneid.calm.com/calm_oneid';
const getJwtExpirationRemainingTime = (
    jwt: string | undefined
): { diff: number; now: number; exp: number; oneid?: string } | undefined => {
    if (!jwt) {
        return undefined;
    }

    try {
        const decodedJwt = jwtDecode<JwtPayload & { [ONEID_KEY]?: string }>(
            jwt
        );
        if (!decodedJwt.exp) {
            return undefined;
        }
        const exp = decodedJwt.exp * SECOND_MS;

        const now = new Date().getTime();
        const diff = exp - now;
        return {
            diff,
            now,
            exp,
            oneid: decodedJwt[ONEID_KEY],
        };
    } catch {
        return undefined;
    }
};

function wrapGetAccessTokenSilently(
    getAccessTokenSilently: GetAccessTokenSilently,
    push: Push,
    currentPath: string
): GetAccessTokenSilently {
    function wrappedGetAccessTokenSilently(
        getTokenOptions: GetTokenSilentlyOptions & { detailedResponse: true }
    ): Promise<GetTokenSilentlyVerboseResponse>;
    function wrappedGetAccessTokenSilently(
        getTokenOptions?: GetTokenSilentlyOptions
    ): Promise<string>;
    function wrappedGetAccessTokenSilently(
        getTokenOptions: GetTokenSilentlyOptions
    ): Promise<GetTokenSilentlyVerboseResponse | string>;
    async function wrappedGetAccessTokenSilently(
        getTokenOptions?: GetTokenSilentlyOptions
    ): Promise<GetTokenSilentlyVerboseResponse | string> {
        try {
            const result = await getAccessTokenSilently(getTokenOptions);
            const expirationDetails = getJwtExpirationRemainingTime(result);
            if (expirationDetails && expirationDetails.diff < 0) {
                logger.warn(
                    'Auth0 access token expired immediately after retrieval',
                    expirationDetails
                );
            }
            return result;
        } catch (e) {
            const auth0Error = e as GenericError;
            const errorsPromptingLogin = ['login_required', 'consent_required'];
            if (
                errorsPromptingLogin.includes(auth0Error.error) &&
                !isLogoutRedirectSuppressed()
            ) {
                const loginUrl = buildLoginUrl(currentPath);
                await push(loginUrl);
            }
            throw e;
        }
    }

    return wrappedGetAccessTokenSilently;
}

const startsWithSlash = (str: string) => str.startsWith('/');
const isCalmHealth = (url: string, envBaseUrl: string) => {
    return url.startsWith(envBaseUrl + '/');
};

const isLogin = (str: string) => /\/login/.test(str);

function sanitizeRedirect(envBaseUrl: string, redirect?: string | null) {
    if (!redirect) {
        return;
    }

    if (isLogin(redirect)) {
        return '/';
    }

    if (startsWithSlash(redirect) || isCalmHealth(redirect, envBaseUrl)) {
        return redirect;
    }
    return '/';
}

function sanitizeAppState(
    appState: RedirectLoginOptions['appState'],
    baseUrl: string
) {
    if (!appState) {
        return;
    }

    return {
        ...appState,
        returnTo: sanitizeRedirect(baseUrl, appState.returnTo),
    };
}

function sanitizeAuthorizationParams(
    envBaseUrl: string,
    authorizationParams: RedirectLoginOptions['authorizationParams']
) {
    if (!authorizationParams?.redirect_uri) {
        return authorizationParams;
    }

    const url = new URL(authorizationParams.redirect_uri);
    const baseUrl = url.origin + url.pathname;
    const searchParams = url.searchParams;
    const redirect = searchParams.get('redirect');

    if (!redirect) {
        return authorizationParams;
    }

    const sanitizedRedirect = sanitizeRedirect(envBaseUrl, redirect);
    if (sanitizedRedirect) {
        searchParams.set('redirect', sanitizedRedirect);
    }

    // base url plus search params
    const newRedirectUri = `${baseUrl}?${searchParams.toString()}`;
    return {
        ...authorizationParams,
        redirect_uri: newRedirectUri,
    };
}

const useAuth = () => {
    const { push, asPath } = useRouter();
    const auth0 = useAuth0();
    const { NEXT_PUBLIC_SITE_BASEURL: envBaseUrl } = useAppConfig();
    const wrappedAuth0 = useMemo(() => {
        const { getAccessTokenSilently } = auth0;
        return {
            ...auth0,
            /**
             * Low-level method with no external dependencies to sanitize the redirects passed in
             * @see useLogin hook if you want to login
             * */
            loginWithRedirect: async ({
                appState,
                authorizationParams,
                ...options
            }: RedirectLoginOptions) => {
                const sanitizedAppState = sanitizeAppState(
                    appState,
                    envBaseUrl
                );
                const sanitizedAuthorizationParams =
                    sanitizeAuthorizationParams(
                        envBaseUrl,
                        authorizationParams
                    );
                return auth0.loginWithRedirect({
                    ...options,
                    appState: sanitizedAppState,
                    authorizationParams: sanitizedAuthorizationParams,
                });
            },
            getAccessTokenSilently: wrapGetAccessTokenSilently(
                getAccessTokenSilently,
                push,
                asPath
            ),
        };
    }, [asPath, auth0, envBaseUrl, push]);
    const e2eAuthState = useE2eAuth();
    return e2eAuthState ?? wrappedAuth0;
};

export default useAuth;
